import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, Building2, CalendarCheck, BarChart3, FileText, Trophy, Settings, LogOut, Repeat2, ShieldCheck, Wallet, X, Users, Megaphone, Music } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { classNames } from '../../utils/formatters';

const navigation = [
  { name: 'Dashboard', to: '/dashboard', icon: LayoutDashboard },
  { name: 'Bookings',  to: '/bookings',  icon: CalendarCheck },
  { name: 'My Venues', to: '/venues',    icon: Building2 },
  { name: 'Open Play', to: '/openplays', icon: Trophy },
  { name: 'Step Dance', to: '/step-dance', icon: Music },
  { name: 'Customer CRM', to: '/crm', icon: Megaphone },
  { section: 'Reports' },
  { name: 'Financial & Performance', to: '/reports', icon: BarChart3, exact: true },
  { name: 'Revenue by Activity', to: '/reports/revenue-by-activity', icon: BarChart3 },
  { name: 'Payment Transactions', to: '/reports/payment-transactions', icon: FileText },
  { name: 'Booking Details', to: '/reports/booking-details', icon: FileText },
  { name: 'Payouts',   to: '/payouts',   icon: Wallet },
  { name: 'Settings',  to: '/settings',  icon: Settings },
  { name: 'Users', to: '/users', icon: Users },
];

const sessionNavigation = navigation.filter(item => ['/openplays', '/step-dance'].includes(item.to));

const staffNavigation = [
  { name: 'Bookings', to: '/bookings', icon: CalendarCheck },
];

export default function Sidebar({ open, onClose }) {
  const { logout, user, isAdminDelegatedSession, returnToOwnerSelection } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  function switchOwner() {
    if (returnToOwnerSelection()) navigate('/select-owner');
    onClose();
  }

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-40 bg-[#264D59]/30 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}
      <div
        className={classNames(
          'fixed inset-y-0 left-0 z-50 flex w-64 flex-col bg-[#264D59]',
          'shadow-[2px_0_8px_rgba(0,0,0,.10)]',
          'transition-transform duration-300 lg:translate-x-0 lg:static lg:z-auto',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* ── Brand header ── */}
        <div className="flex h-20 items-center justify-between px-5 border-b border-white/10">
          <div>
            <img
              src="/freac-official-logo.png"
              alt="FREAC official logo"
              className="w-36 rounded-md bg-white p-1 object-contain"
            />
            <span className="mt-1 block text-xs text-white/60">Court Owner Portal</span>
          </div>
          <button
            onClick={onClose}
            className="lg:hidden text-white/60 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* ── Navigation ── */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {isAdminDelegatedSession && (
            <div className="mb-4 rounded-xl border border-[#F9E07F]/30 bg-[#F9E07F]/10 p-3">
              <p className="flex items-center gap-2 text-xs font-semibold text-[#F9E07F]"><ShieldCheck size={14} /> Admin access</p>
              <p className="mt-1 truncate text-xs text-white/70">Viewing {user?.name || user?.email}</p>
              <button type="button" onClick={switchOwner} className="mt-2 flex items-center text-xs font-semibold text-white hover:underline">
                <Repeat2 size={13} className="mr-1.5" /> Switch owner
              </button>
            </div>
          )}
          {(user?.role === 'OpenPlayOrganiser' ? sessionNavigation : user?.role === 'BookingStaff' ? [...staffNavigation, ...(user?.hasOpenPlayOrganiserAccess ? sessionNavigation : [])] : user?.role === 'VenueManager' ? navigation.filter((item) => !['/users', '/crm'].includes(item.to)) : navigation).map((item) => {
            if (item.section) {
              return <p key={item.section} className="px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-white/40">{item.section}</p>;
            }
            const isActive =
              location.pathname === item.to ||
              (!item.exact && item.to !== '/dashboard' && location.pathname.startsWith(item.to));

            return (
              <NavLink
                key={item.name}
                to={item.to}
                onClick={onClose}
                className={classNames(
                  isActive
                    ? 'bg-white/15 text-[#F9E07F] font-semibold'
                    : 'text-white/60 hover:text-white hover:bg-white/10',
                  'group flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-colors duration-200'
                )}
              >
                <item.icon
                  className={classNames(
                    isActive ? 'text-[#F9E07F]' : 'text-white/40 group-hover:text-white/60',
                    'mr-3 h-5 w-5 flex-shrink-0'
                  )}
                />
                {item.name}
              </NavLink>
            );
          })}
        </nav>

        {/* ── Logout ── */}
        <div className="border-t border-white/10 p-3">
          <button
            onClick={logout}
            className="flex w-full items-center px-3 py-2.5 text-sm font-medium
              text-white/60 rounded-xl hover:text-white hover:bg-white/10 transition-colors duration-200"
          >
            <LogOut className="mr-3 h-5 w-5 text-white/40" />
            Logout
          </button>
        </div>
      </div>
    </>
  );
}
